import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { UPDATE_ASSET_MUTATION } from '@/apollo/schemas/assets/updateAsset';
import { useAuth } from '@/context/CoginitoAuthProvider';
import { useToast } from '@/hooks/use-toast';
import { IAsset } from '@/types/asset.types';
import { chainLabel, explorerAddressUrl } from '@/utils/chains';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';

const formSchema = z.object({
  name: z.string().min(1, 'Asset name is required'),
  type: z.string().min(1, 'Asset type is required'),
  gold_weight: z.coerce
    .number()
    .positive('Gold weight must be a positive number'),
  copper_weight: z.coerce
    .number()
    .positive('Copper weight must be a positive number'),
  copper_price: z.coerce.number().min(0).optional().or(z.literal(0)),
  certificateUrl: z
    .string()
    .url('Must be a valid URL')
    .optional()
    .or(z.literal('')),
  imageUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  pricePerToken: z.coerce.number().min(0).optional().or(z.literal(0)),
  tokenizedPercentage: z.coerce.number().min(0).optional().or(z.literal(0)),
  stageMultiplier: z.coerce.number().min(0).optional().or(z.literal(0)),
  totalTokens: z.coerce.number().min(0).optional().or(z.literal(0)),
  minimumInvestmentAmount: z.coerce.number().min(0).optional().or(z.literal(0)),
  isActive: z.boolean().default(true),
  requireAccreditedInvestor: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

interface EditAssetDialogProps {
  asset: IAsset;
  refetchData: () => void;
}

const EditAssetDialog = ({ asset, refetchData }: EditAssetDialogProps) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { session } = useAuth();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: '',
      gold_weight: 0,
      copper_weight: 0,
      copper_price: 0,
      certificateUrl: '',
      imageUrl: '',
      pricePerToken: 0,
      tokenizedPercentage: 0,
      stageMultiplier: 0,
      totalTokens: 0,
      minimumInvestmentAmount: 0,
      isActive: true,
      requireAccreditedInvestor: false,
    },
  });

  // Accredited-only can be switched on for an asset already selling, never off: switching off
  // needs ACE's accredited policy detached from the token, which ACE does not handle yet.
  const alreadyAccredited = !!asset.requireAccreditedInvestor;
  // Switching on is confirmed first: it cannot be undone and it changes the flow for every
  // investor in this offering.
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);

  useEffect(() => {
    if (open && asset) {
      form.reset({
        name: asset.name || '',
        type: asset.type || '',
        gold_weight: asset.gold_weight || 0,
        copper_weight: asset.copper_weight || 0,
        copper_price: asset.copper_price || 0,
        certificateUrl: asset.certificateUrl || '',
        imageUrl: asset.imageUrl || '',
        pricePerToken: asset.pricePerToken || 0,
        tokenizedPercentage: asset.tokenizedPercentage || 0,
        stageMultiplier: asset.stageMultiplier || 0,
        totalTokens: asset.totalTokens || 0,
        minimumInvestmentAmount: 0,
        isActive: !!asset.isActive,
        requireAccreditedInvestor: !!asset.requireAccreditedInvestor,
      });
    }
  }, [open, asset, form]);

  const [updateAsset, { loading }] = useMutation(UPDATE_ASSET_MUTATION, {
    onCompleted: (data) => {
      if (data?.updateAsset?.statusCode === 200) {
        toast({
          variant: 'success',
          title: 'Asset Updated',
          description: 'Asset has been updated successfully.',
        });
        setOpen(false);
        refetchData();
      } else {
        toast({
          variant: 'destructive',
          title: 'Update Failed',
          description: data?.updateAsset?.message || 'Failed to update asset.',
        });
      }
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: error.message,
      });
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (
      values.requireAccreditedInvestor &&
      !alreadyAccredited &&
      !pendingValues
    ) {
      setPendingValues(values);
      return;
    }
    setPendingValues(null);
    if (!session) {
      toast({
        variant: 'destructive',
        title: 'Authentication Required',
        description: 'Please sign in to update an asset.',
      });
      return;
    }

    await updateAsset({
      variables: {
        id: asset.id,
        name: values.name,
        type: values.type,
        gold_weight: values.gold_weight,
        copper_weight: values.copper_weight,
        copper_price: values.copper_price || null,
        certificateUrl: values.certificateUrl || null,
        imageUrl: values.imageUrl || null,
        pricePerToken: values.pricePerToken || null,
        tokenizedPercentage: values.tokenizedPercentage || null,
        stageMultiplier: values.stageMultiplier || null,
        totalTokens: values.totalTokens || null,
        minimumInvestmentAmount: values.minimumInvestmentAmount || null,
        isActive: values.isActive,
        // Sent only when switching on; the backend refuses switching off.
        requireAccreditedInvestor:
          values.requireAccreditedInvestor && !alreadyAccredited ? true : null,
      },
      context: {
        headers: {
          identity: session?.getIdToken().getJwtToken(),
        },
      },
    });
  };

  // From the API, because only the backend knows which explorer this asset's chain uses.
  const tokenExplorerUrl =
    asset.explorerUrl ??
    explorerAddressUrl(
      asset.chain,
      asset.tokenRef ?? asset.erc3643?.tokenAddress ?? null,
    );

  const handleDialogChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      form.reset();
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Pencil size={16} className="mr-2" />
        Edit
      </Button>

      <Dialog open={open} onOpenChange={handleDialogChange}>
        <DialogContent className="bg-white max-h-[90vh] max-w-2xl flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 overflow-y-auto pr-2"
            >
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Asset Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter asset name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Gold, Silver" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gold_weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gold Weight (oz)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter gold weight"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="copper_weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Copper Weight (lbs)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter copper weight"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="copper_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Copper Price</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter copper price"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pricePerToken"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price Per Token</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter price per token"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="totalTokens"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Tokens</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter total tokens"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tokenizedPercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tokenized Percentage</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter tokenized percentage"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="stageMultiplier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stage Multiplier</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter stage multiplier"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="minimumInvestmentAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Investment Amount</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Enter minimum investment"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="certificateUrl"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Certificate URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com/certificate.pdf"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="imageUrl"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>Image URL</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com/image.png"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Read-only, deliberately.

                    The token is already deployed by the time an asset can be edited, so
                    changing this would not move it — it would only make the record disagree
                    with the network the token is actually on, which is how a mint ends up
                    addressed to the wrong chain. The API rejects the field for the same
                    reason. Shown rather than hidden because "which chain is this on" is the
                    first thing an operator needs when reading the row. */}
                <FormItem className="col-span-2">
                  <FormLabel>Chain</FormLabel>
                  <div className="flex items-center gap-2 rounded-md border border-input bg-muted px-3 py-2 text-sm">
                    <Badge variant="outline">{chainLabel(asset.chain)}</Badge>
                    <span className="text-muted-foreground">
                      {asset.tokenStandard ?? 'ERC3643'}
                    </span>
                    {tokenExplorerUrl && (
                      <a
                        href={tokenExplorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto text-blue-700 underline"
                      >
                        View token
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Set at creation and not editable — the token is already
                    deployed.
                  </p>
                </FormItem>
              </div>

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => {
                  // console.log(field.value);

                  return (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel>Active Status</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Set whether this asset is active or inactive{' '}
                          {field.value}
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="requireAccreditedInvestor"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <FormLabel>Accredited investors only</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        {alreadyAccredited
                          ? 'This offering is for accredited investors only. It cannot be switched back yet: ACE cannot remove the accredited policy from a token cleanly (a Chainlink-side fix is pending).'
                          : 'Turn on to make this offering accredited-only. The ACE accredited policy is added to the token. Investors part-way through are asked for the accreditation letter before funding, and delivery waits until their letter is approved. Cannot be turned off afterwards. Needs an ACE compliance mode.'}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                        disabled={alreadyAccredited}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Asset'
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pendingValues}
        onOpenChange={(isOpen) => !isOpen && setPendingValues(null)}
      >
        <DialogContent className="sm:max-w-[460px] bg-white dark:bg-foreground dark:text-white">
          <DialogHeader>
            <DialogTitle>Make this offering accredited-only?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              <span className="font-medium text-foreground">{asset.name}</span>{' '}
              becomes open to accredited investors only:
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                The ACE accredited policy is added to the token (confirmed
                on-chain within about half an hour).
              </li>
              <li>
                Investors who have not funded yet are asked for the
                accreditation letter before funding.
              </li>
              <li>
                Investors who already paid are emailed, and their tokens are
                delivered only once their letter is approved.
              </li>
            </ul>
            <p className="font-medium text-foreground">
              This cannot be turned off afterwards.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingValues(null)}
            >
              Keep as is
            </Button>
            <Button
              type="button"
              disabled={loading}
              onClick={() => pendingValues && onSubmit(pendingValues)}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Make accredited-only
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditAssetDialog;
